import { Domain } from 'auth-common/domain';
import dayjs from 'dayjs';
import type { components } from '../../generated/apiClient';
import type { AuthInfo } from '../../hook/model/authInfo';
import * as AccountConverter from './account';

const requestedProjectContextFromApi = (
  api: components['schemas']['AuthInfo.RequestedProjectContext'],
): AuthInfo.RequestedProjectContext => ({
  organization: {
    id: Domain.Object.Organization.Id.FromString(api.organization.id),
    name: api.organization.name,
    profileImageSrc: api.organization.profileImageSrc,
    membershipRole: api.organization.membershipRole,
    authRequirements: api.organization.authRequirements,
  },
  project: {
    id: Domain.Object.Project.Id.FromString(api.project.id),
    name: api.project.name,
    productSettings: api.project.productSettings,
    featureFlags: api.project.featureFlags,
    authRequirements: api.project.authRequirements,
    membership: api.project.membership
      ? {
          ...(api.project.membership.group
            ? {
                group: {
                  id: Domain.Object.Group.Id.FromString(api.project.membership.group.id),
                  name: api.project.membership.group.name,
                  policy: Domain.Object.Group.Policy.Construct(api.project.membership.group.policy),
                  isProjectAdmin: api.project.membership.group.isProjectAdmin,
                },
              }
            : {}),
        }
      : null,
    access:
      api.project.access.kind === 'membership'
        ? { kind: 'membership' }
        : {
            kind: 'system_admin_product_entry',
            ...(api.project.access.activeGrantId
              ? { activeGrantId: Domain.Object.Account.Grant.Id.FromString(api.project.access.activeGrantId) }
              : {}),
            ...(api.project.access.expiresAt ? { expiresAt: dayjs(api.project.access.expiresAt) } : {}),
          },
    hasPiiAccessNow: api.project.hasPiiAccessNow,
  },
});

export const fromApi = (api: components['schemas']['AuthInfo']): AuthInfo => {
  return {
    account: AccountConverter.fromApi(api.Account),
    organizations: api.Organization.map((o) => {
      return {
        id: Domain.Object.Organization.Id.FromString(o.id),
        name: o.name,
        profileImageSrc: o.profileImageSrc,
        role: o.role,
        authRequirements: o.authRequirements,
        projects: o.projects.map((p) => ({
          id: Domain.Object.Project.Id.FromString(p.id),
          ...(p.publicKey ? { publicKey: Domain.Object.Project.PublicKey.FromString(p.publicKey) } : {}),
          name: p.name,
          productSettings: p.productSettings,
          group: p.group
            ? {
                id: Domain.Object.Group.Id.FromString(p.group.id),
                name: p.group.name,
                policy: Domain.Object.Group.Policy.Construct(p.group.policy),
                isProjectAdmin: p.group.isProjectAdmin,
              }
            : null,
          hasPiiAccessNow: p.hasPiiAccessNow,
          authRequirements: p.authRequirements,
        })),
      };
    }),
    ...(api.RequestedProjectContext ? { requestedProjectContext: requestedProjectContextFromApi(api.RequestedProjectContext) } : {}),
  };
};
